import useApi from "@/hooks/useApi";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  CannedResponse,
  Column,
  Edit01,
  NewCannedResponse,
  Plus,
  SecondaryGrayButton,
  TableBase,
  Tooltip,
  Trash01
} from "@velaro/velaro-component-lib";
import useTeams from "@/hooks/useTeams";
import useUsers from "@/hooks/useUsers";
import CannedResponseRightAsideEditor from "./CannedResponseRightAsideEditor";
import CannedResponseRightAsideImporter from "./CannedResponseRightAsideImporter";
import BulkActionBar from "@/components/BulkActionBar";

interface CannedResponseTableItem {
  shortcut: string;
  message: string;
  access: string;
  users: ReactElement<any, any>;
  actions: ReactElement<any, any>;
}

const tableColumns: Column[] = [
  {
    label: "Shortcut",
    accessor: "shortcut"
  },
  {
    label: "Message",
    accessor: "message"
  },
  {
    label: "Access",
    accessor: "access"
  },
  {
    label: "Users",
    accessor: "users",
    disableSort: true
  },
  {
    label: "Actions",
    accessor: "actions",
    disableSort: true
  }
];

const defaultCannedResponse: NewCannedResponse = {
  isGlobal: false,
  messages: [""],
  shortcut: ""
};

export default function CannedResponses() {
  const { getDisplayNameForUser } = useUsers();
  const { getTeamNameById } = useTeams();
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [importing, setImporting] = useState<boolean>(false);
  const [selectedIndicies, setSelectedIndicies] = useState<number[]>([]);

  const api = useApi();
  const [loading, setLoading] = useState(true);

  const fetchResponses = useCallback(async () => {
    const response = await api.messaging.get("CannedResponses/List/Site");
    setResponses(await response.json());
    setLoading(false);
  }, [api.messaging]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const deleteMessage = useCallback(
    async (cannedResponseId: number) => {
      if (confirm("Are you sure you want to delete this canned response?")) {
        await api.messaging.delete("CannedResponses", {
          ResponseIds: [cannedResponseId]
        });
        await fetchResponses();
      }
    },
    [api.messaging, fetchResponses]
  );

  async function bulkDelete() {
    if (
      confirm("Are you sure you want to delete the selected canned responses?")
    ) {
      const ids = selectedIndicies.map((i) => responses[i].id);
      await api.messaging.delete("CannedResponses", {
        ResponseIds: ids
      });
      setSelectedIndicies([]);
      setResponses((prevResponses) =>
        prevResponses.filter((_, index) => !selectedIndicies.includes(index))
      );
    }
  }

  const tableData = useMemo(() => {
    function getTeamsTooltip(teamsIds: number[]): string {
      return teamsIds
        .map((teamId) => {
          return getTeamNameById(teamId) || "Unknown Team";
        })
        .join(", ");
    }

    function getUsersColumn(response: CannedResponse): ReactElement<any, any> {
      if (response.isGlobal) {
        return <span>Everyone</span>;
      }

      const teamCount = response.teamIds?.length || 0;
      if (teamCount > 0) {
        return (
          <Tooltip
            text={getTeamsTooltip(response.teamIds || [])}
            direction="top"
          >
            <span className="cursor-pointer text-blue-500">
              {teamCount} {teamCount === 1 ? "Team" : "Teams"}
            </span>
          </Tooltip>
        );
      }

      if (response.userId) {
        return <span>{getDisplayNameForUser(response.userId)}</span>;
      }

      return <span>No access</span>;
    }

    const rows: CannedResponseTableItem[] = [];

    responses.forEach((response, i) => {
      const hasTeams = response.teamIds && response.teamIds.length > 0;
      rows.push({
        shortcut: `/${response.shortcut}`,
        message: response.message,
        access: hasTeams || response.isGlobal ? "Shared" : "Private",
        users: getUsersColumn(response),
        actions: (
          <div className="flex gap-6 text-slate-500 items-center justify-center">
            {!response.userId && (
              <button className="underline" onClick={() => setEditingIndex(i)}>
                <Edit01 />
              </button>
            )}
            <button
              className="underline"
              onClick={() => deleteMessage(response.id)}
            >
              <Trash01 />
            </button>
          </div>
        )
      });
    });

    return rows;
  }, [deleteMessage, getDisplayNameForUser, getTeamNameById, responses]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Canned Responses</h1>
          <p className="text-xs text-slate-400 mt-0.5">Save and reuse common replies to speed up agent workflows</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
            onClick={() => setImporting(true)}
          >
            <Plus />
            Import CSV
          </button>
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
            onClick={() => setEditingIndex(-1)}
          >
            <Plus />
            Add New
          </button>
        </div>
      </div>
      <div className="flex items-end justify-start pb-8">
        {/* <div>
          <DefaultTabs
            className="px-6"
            label={"Private"}
            active={selectedTab === "private"}
            onClick={() => setSelectedTab("private")}
          />
        </div>
        <div>
          <DefaultTabs
            className="px-6"
            label={"Shared"}
            active={selectedTab === "shared"}
            onClick={() => {
              setSelectedTab("shared");
            }}
          />
        </div> */}
        <div className="border-b-2 h-full border-slate-200 w-full"></div>
      </div>
      <div className="pb-4 w-full">
        {selectedIndicies.length > 0 && (
          <BulkActionBar>
            <div>
              ({selectedIndicies.length}){" "}
              {selectedIndicies.length === 1 ? "response" : "responses"}{" "}
              selected
            </div>
            <div className="flex items-center gap-4">
              <SecondaryGrayButton
                prefixIcon={<Trash01 />}
                onClick={bulkDelete}
                label="Delete"
              />
            </div>
          </BulkActionBar>
        )}
      </div>
      <div className="overflow-auto h-full">
        <TableBase
          columns={tableColumns}
          data={tableData}
          onChangeSelection={setSelectedIndicies}
        />
      </div>
      {editingIndex != null && editingIndex >= 0 && (
        <CannedResponseRightAsideEditor
          onClose={() => setEditingIndex(null)}
          cannedResonse={responses[editingIndex]}
          onSave={(updatedResponses) => {
            setResponses((prevResponses) =>
              prevResponses.map((resp, i) =>
                i === editingIndex ? updatedResponses[0] : resp
              )
            );
            setEditingIndex(null);
          }}
        />
      )}
      {editingIndex != null && editingIndex === -1 && (
        <CannedResponseRightAsideEditor
          onClose={() => setEditingIndex(null)}
          cannedResonse={defaultCannedResponse}
          onSave={(newResponses) => {
            setResponses((prevResponses) => [
              ...prevResponses,
              ...newResponses
            ]);
            setEditingIndex(null);
          }}
        />
      )}
      {importing && (
        <CannedResponseRightAsideImporter
          onInport={(responses: CannedResponse[]) => {
            setResponses((prevResponses) => [...prevResponses, ...responses]);
            setImporting(false);
          }}
          onClose={() => setImporting(false)}
        />
      )}
    </div>
  );
}
